<script setup lang="ts">
/* ============================================================
   HomeView — the returning-visit home: a command bar

   One box does both jobs: type to search every reviewed document
   (names, text, and comments — see docSearch.ts), or paste markdown
   to start a new review. Under it, the other ways in (upload,
   clipboard, connect your agent) as pills, then "Jump back in": the
   five most recent docs, which turns into search results as you type.

   Keyboard: `/` focuses the box (typing any letter does too), 1–5
   opens a recent doc, ↑/↓ + Enter pick a result, Esc clears.
   Only while `active` — Reader keeps this view mounted under v-show.

   The first-run surface is a different document — the Welcome.md tab
   built by useSpecialTabs. Reader picks between them by whether any
   history exists.
   ============================================================ */

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { Tab } from '@/types'
import { hashColor, relativeTime } from '@/composables/utils'
import { indexDoc, searchDocs, type SearchResult } from '@/composables/docSearch'

const props = defineProps<{ recent: readonly Tab[]; active: boolean }>()

const emit = defineEmits<{
  (e: 'open', hash: string): void
  (e: 'clear'): void
  (e: 'open-file'): void
  (e: 'paste-clipboard'): void
  (e: 'paste-text', text: string): void
  (e: 'install'): void
}>()

const RECENT_COUNT = 5

// The full prompt doesn't fit a phone-width box.
const narrow = window.matchMedia('(max-width: 760px)')
const compact = ref(narrow.matches)
const onNarrow = (e: MediaQueryListEvent) => (compact.value = e.matches)
const placeholder = computed(() =>
  compact.value ? 'Search, or paste markdown' : 'Search your plans, or paste markdown to start a review',
)

const query = ref('')
const input = ref<HTMLInputElement | null>(null)
const selected = ref(0)

const searching = computed(() => query.value.trim() !== '')
// Lazy: only built once someone searches, and again when history changes.
const index = computed(() => props.recent.map(indexDoc))

const rows = computed<SearchResult[]>(() => {
  if (searching.value) return searchDocs(index.value, query.value)
  return props.recent.slice(0, RECENT_COUNT).map((doc) => ({
    doc,
    hits: { name: 0, text: 0, comments: 0 },
    snippetKind: null,
    snippet: [],
  }))
})

watch(query, () => (selected.value = 0))

function commentStatus(tab: Tab): string {
  const all = tab.comments || []
  const resolved = all.filter((c) => c.resolved).length
  const open = all.length - resolved
  if (!all.length) return 'no comments'
  if (!resolved) return `${open} open`
  if (!open) return `${resolved} resolved`
  return `${open} open · ${resolved} resolved`
}

function hitsText(r: SearchResult): string {
  const parts: string[] = []
  if (r.hits.text) parts.push(`${r.hits.text} in text`)
  if (r.hits.comments) parts.push(`${r.hits.comments} in review`)
  return parts.join(' · ')
}

function open(hash: string): void {
  query.value = ''
  input.value?.blur()
  emit('open', hash)
}

function onInputKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    e.preventDefault()
    if (query.value) query.value = ''
    else input.value?.blur()
  } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    if (!rows.value.length) return
    e.preventDefault()
    const n = rows.value.length
    selected.value = (selected.value + (e.key === 'ArrowDown' ? 1 : n - 1)) % n
  } else if (e.key === 'Enter') {
    const row = rows.value[selected.value]
    if (searching.value && row) {
      e.preventDefault()
      open(row.doc.hash)
    }
  }
}

// Multi-line text is markdown to review, not a search query.
function onInputPaste(e: ClipboardEvent): void {
  const text = e.clipboardData?.getData('text/plain') ?? ''
  if (!text.includes('\n') || !text.trim()) return
  e.preventDefault()
  query.value = ''
  emit('paste-text', text)
}

function onKeydown(e: KeyboardEvent): void {
  if (!props.active || e.metaKey || e.ctrlKey || e.altKey) return
  if (e.target instanceof Element && e.target.closest('input, textarea, select, [contenteditable="true"]')) return
  if (e.key === '/') {
    e.preventDefault()
    input.value?.focus()
    return
  }
  if (/^[1-9]$/.test(e.key)) {
    const row = rows.value[Number(e.key) - 1]
    if (row && Number(e.key) <= RECENT_COUNT) {
      e.preventDefault()
      open(row.doc.hash)
    }
    return
  }
  // Start typing anywhere to search: focus now and the key lands in the box.
  if (e.key.length === 1 && e.key !== ' ') input.value?.focus()
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
  narrow.addEventListener('change', onNarrow)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  narrow.removeEventListener('change', onNarrow)
})
</script>

<template>
  <section class="home-view">
    <div class="home-scroll">
      <div class="home-col">
        <h1 class="home-title">Welcome back</h1>

        <label class="home-command">
          <svg class="home-command-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            ref="input"
            v-model="query"
            class="home-command-input"
            type="text"
            spellcheck="false"
            autocomplete="off"
            :placeholder="placeholder"
            aria-label="Search your plans, or paste markdown to start a review"
            @keydown="onInputKeydown"
            @paste="onInputPaste"
          />
          <button v-if="query" class="btn btn-ghost btn-icon home-command-clear" aria-label="Clear search" @click="query = ''">
            ✕
          </button>
          <kbd v-else class="home-kbd home-hint">/</kbd>
        </label>

        <div class="home-pills">
          <button class="btn btn-default btn-md home-pill" @click="emit('open-file')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M12 16V4M7 9l5-5 5 5" />
              <path d="M4 16v4h16v-4" />
            </svg>
            Upload file
          </button>
          <button class="btn btn-default btn-md home-pill" @click="emit('paste-clipboard')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="8" y="3" width="8" height="4" rx="1" />
              <path d="M8 5H6v16h12V5h-2" />
            </svg>
            From clipboard
          </button>
          <button class="btn btn-default btn-md home-pill" @click="emit('install')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" aria-hidden="true">
              <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
            </svg>
            Connect your agent
          </button>
        </div>
        <p class="home-drop">or drop a .md file anywhere on this page</p>

        <div class="home-list-head">
          <h2 class="home-list-title">
            {{ searching ? `${rows.length} ${rows.length === 1 ? 'match' : 'matches'}` : 'Jump back in' }}
          </h2>
          <span class="home-hint">{{ searching ? '↑ ↓ to pick · Enter to open' : `Press 1–${Math.min(RECENT_COUNT, rows.length)} to open` }}</span>
        </div>

        <div class="home-list">
          <div v-if="!rows.length" class="home-empty">
            {{ searching ? 'Nothing matches — try fewer words.' : 'No documents yet.' }}
          </div>
          <button
            v-for="(r, i) in rows"
            :key="r.doc.hash"
            class="home-row"
            :class="{ 'is-selected': searching && i === selected }"
            @click="open(r.doc.hash)"
            @mousemove="selected = i"
          >
            <kbd v-if="!searching" class="home-kbd home-row-key home-hint">{{ i + 1 }}</kbd>
            <span class="hash-dot" :style="{ '--dot': hashColor(r.doc.hash) }"></span>
            <span class="home-row-name" :title="r.doc.filename">{{ r.doc.filename }}</span>
            <span class="home-row-meta">{{ commentStatus(r.doc) }}</span>
            <span class="home-row-when">{{ relativeTime(r.doc.updatedAt) }}</span>
            <span v-if="r.snippet.length" class="home-snippet">
              <span class="home-snippet-where">{{ r.snippetKind === 'comments' ? 'Review' : 'Text' }}</span>
              <span class="home-snippet-text"
                ><template v-for="(part, j) in r.snippet" :key="j"
                  ><mark v-if="part.hit">{{ part.text }}</mark
                  ><template v-else>{{ part.text }}</template></template
                ></span
              >
              <span class="home-snippet-hits">{{ hitsText(r) }}</span>
            </span>
          </button>
        </div>

        <div v-if="recent.length" class="home-foot">
          <span>{{ recent.length }} {{ recent.length === 1 ? 'document' : 'documents' }} in this browser</span>
          <button class="btn btn-ghost btn-sm home-clear" @click="emit('clear')">Clear history</button>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.home-view {
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: var(--bg);
}
.home-scroll {
  height: 100%;
  overflow-y: auto;
}
.home-col {
  width: min(720px, 100%);
  margin: 0 auto;
  padding: 72px var(--space-6) 96px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}
.home-title {
  margin: 0 0 var(--space-6);
  text-align: center;
  font-size: 36px;
  font-weight: 650;
  letter-spacing: -0.03em;
  line-height: 1.1;
  color: var(--text);
}

/* ---- the command box ---- */
.home-command {
  display: flex;
  align-items: center;
  gap: 14px;
  height: 60px;
  padding: 0 14px 0 20px;
  background: var(--bg-elev);
  border: 1px solid var(--border-strong);
  border-radius: 14px;
  box-shadow: var(--shadow-md);
  cursor: text;
  transition: border-color 120ms;
}
.home-command:focus-within {
  border-color: var(--text-faint);
}
.home-command-icon {
  width: 20px;
  height: 20px;
  flex: none;
  color: var(--text-muted);
}
.home-command-input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  font: inherit;
  font-size: 17px;
  color: var(--text);
}
.home-command-input:focus {
  outline: none;
}
.home-command-input::placeholder {
  color: var(--text-faint);
}
.home-kbd {
  font-family: var(--mono-font);
  font-size: 12px;
  padding: 2px 8px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  line-height: 1.4;
}

/* ---- other ways in ---- */
.home-pills {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-top: var(--space-4);
}
.home-pill {
  border-radius: var(--radius-pill);
  gap: var(--space-2);
  font-weight: 400;
}
.home-pill svg {
  width: 14px;
  height: 14px;
}
.home-drop {
  margin: 14px 0 44px;
  text-align: center;
  font-size: var(--text-md);
  color: var(--text-muted);
}

/* ---- jump back in / results ---- */
.home-list-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: var(--space-2);
}
.home-list-title {
  margin: 0;
  font-size: var(--text-sm);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-muted);
}
.home-list-head .home-hint {
  font-size: var(--text-sm);
  color: var(--text-muted);
}
.home-list {
  display: flex;
  flex-direction: column;
}
.home-row {
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr) auto auto;
  align-items: center;
  column-gap: 14px;
  row-gap: 6px;
  width: 100%;
  padding: 11px var(--space-2);
  text-align: left;
  border-bottom: 1px solid var(--border);
  font-size: var(--text-base);
  color: var(--text);
  cursor: pointer;
}
.home-row:hover,
.home-row.is-selected {
  background: var(--bg-hover);
}
/* Results have no number key; keep the dot in the first column. */
.home-row:not(:has(.home-row-key)) {
  grid-template-columns: auto minmax(0, 1fr) auto auto;
}
.home-row-key {
  width: 22px;
  height: 22px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  box-sizing: border-box;
}
.home-row-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.home-row-meta,
.home-row-when {
  font-size: var(--text-sm);
  color: var(--text-muted);
  white-space: nowrap;
}
.home-row-when {
  min-width: 64px;
  text-align: right;
}
.home-snippet {
  grid-column: 2 / -1;
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
  min-width: 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
}
.home-snippet-where {
  flex: none;
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-faint);
}
.home-snippet-text {
  flex: 1;
  min-width: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.45;
}
.home-snippet-text mark {
  background: var(--highlight);
  color: var(--text);
  border-radius: 2px;
}
.home-snippet-hits {
  flex: none;
  white-space: nowrap;
  color: var(--text-faint);
}
.home-empty {
  padding: 32px;
  text-align: center;
  font-size: var(--text-md);
  color: var(--text-muted);
}
.home-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: var(--space-4);
  font-size: var(--text-sm);
  color: var(--text-faint);
}
.home-clear:hover {
  color: var(--danger);
}

/* Phones: no physical keyboard, so drop the key hints; tighter top. */
@media (max-width: 760px), (hover: none) {
  .home-hint,
  .home-drop {
    display: none;
  }
  .home-pills {
    margin-bottom: 36px;
  }
  .home-row:has(.home-row-key) {
    grid-template-columns: auto minmax(0, 1fr) auto auto;
  }
}
@media (max-width: 760px) {
  .home-col {
    padding: 40px var(--space-4) 64px;
  }
  .home-title {
    font-size: 28px;
  }
  .home-command {
    height: 52px;
    padding-left: 16px;
  }
  .home-command-input {
    font-size: 16px;
  }
  .home-row-meta {
    display: none;
  }
  .home-row,
  .home-row:has(.home-row-key),
  .home-row:not(:has(.home-row-key)) {
    grid-template-columns: auto minmax(0, 1fr) auto;
  }
}
</style>
